// app/frontend/app/lib/batchTransactions.ts
//
// React-free twin of MineralInventoryCalculator for the multi-transaction grid.
// The /inventories row is an already-computed per-STR net position (it does not
// expose separate acq/div components), so we treat its net_acres / weighted_nri
// as the PRE-BATCH baseline and layer only the other in-grid rows for the same
// STR on top (order-independent). Keep in lockstep with
// app/services/mineral_inventory_calculator.rb.
import { normalizeStr } from "@/app/lib/str"
import type { InterestKind, RowState } from "@/app/pages/NewTransactionPage/types"

// Per-kind slice of an /inventories row. Nil server values are compacted away,
// so optional keys are simply absent. NOTE: a missing latest_acq_status is
// ambiguous (no ACQ at all vs latest ACQ with nil status) — key position logic
// off has_position / latest_acq_date, never off status presence.
export interface KindPosition {
  net_acres: string
  weighted_nri?: string
  /** Acre-weighted royalty fraction (mineral slice only). */
  weighted_royalty?: string
  has_position: boolean
  latest_acq_status?: string | null
  latest_acq_date?: string | null
  legal_descs?: string[]
  /** Distinct interest statuses for this kind's live rows (e.g. ["Open","PT"]). */
  interest_types?: string[]
  /** Earliest expiration_date over this kind's kept PT ACQ rows (ISO date). */
  next_expiration?: string | null
}

/** The `kinds` shape carried by /inventories rows. */
export type InventoryKinds = { leasehold: KindPosition; mineral: KindPosition }

/** Whether an /inventories row has a position in the given kind. Rows without
 *  `kinds` (stale cached payloads that predate interest kinds) count as
 *  leasehold-only — their top-level figures are leasehold-accurate. */
export function hasKindPosition(
  kinds: InventoryKinds | undefined,
  kind: "leasehold" | "mineral"
): boolean {
  return kinds ? kinds[kind].has_position : kind === "leasehold"
}

/** Kind-scoped net acres for an /inventories row, with the same stale-cache
 *  fallback as hasKindPosition (top-level figure on leasehold, 0 on mineral). */
export function kindNetAcres(
  row: { net_acres: string; kinds?: InventoryKinds },
  kind: "leasehold" | "mineral"
): string {
  const slice = row.kinds?.[kind]
  if (slice) return slice.net_acres
  return kind === "leasehold" ? row.net_acres : "0"
}

export interface BaseInvRow {
  str: string
  weighted_nri: string
  net_acres: string
  county: string | null
  kinds?: { leasehold: KindPosition; mineral: KindPosition }
}

export interface EffectivePosition {
  naOwned: number
  nriOwned: number
  orriReserved: number // fraction, for the current DIV row
  hasPosition: boolean
  divStatus: string | null // predicted auto-detected DIV status
}

// "Open" mineral acquisitions are unleased, i.e. the full royalty. Payload and
// display share this constant so the stored value and the locked grid cell
// can't drift.
export const FULL_ROYALTY = 1
export const FULL_ROYALTY_DISPLAY = FULL_ROYALTY.toFixed(2)

const num = (v: string) => (v.trim() === "" ? 0 : Number(v))

// Effective position for a row = pre-batch inventory for the STR PLUS every
// OTHER grid row targeting the same STR. NRI baseline comes from the inventory
// row's weighted_nri (acquisitions never lowered by divestitures); in-grid ACQ
// rows contribute additional weighted NRI, DIV rows only reduce net acres.
export function deriveEffectivePosition(
  base: BaseInvRow | null,
  rows: RowState[],
  thisRowId: string,
  kind: InterestKind
): EffectivePosition {
  const thisRow = rows.find((r) => r.id === thisRowId)
  const target = normalizeStr(thisRow?.str ?? "")

  // Baseline comes from the per-kind slice when the API provides it; fall back
  // to the legacy top-level fields (and the old "any nonzero" heuristic for
  // hadBasePosition) so the grid keeps working against a stale API.
  const k = base?.kinds?.[kind]
  const baseNet = k ? Number(k.net_acres) : base ? Number(base.net_acres) : 0
  const baseNri = k
    ? kind === "leasehold"
      ? Number(k.weighted_nri ?? 0)
      : 0
    : base
      ? Number(base.weighted_nri)
      : 0

  let netAcres = baseNet
  // Weighted-NRI accumulator: seed with the pre-batch position (baseNri over
  // baseNet acres), then add in-grid ACQ contributions. Mineral rows carry no
  // NRI, so only leasehold acquisitions contribute.
  let wNri = baseNri * (baseNet > 0 ? baseNet : 0)
  let acqAcres = baseNet > 0 ? baseNet : 0
  let hadBasePosition = k ? k.has_position : baseNri > 0 || baseNet !== 0

  if (target) {
    for (const r of rows) {
      if (r.id === thisRowId) continue
      if (normalizeStr(r.str) !== target) continue
      const net = num(r.net_acres)
      if (r.mode === "ACQ") {
        netAcres += net
        if (kind === "leasehold") wNri += (num(r.nri_percent) / 100) * net
        acqAcres += net
        if (net !== 0) hadBasePosition = true
      } else {
        netAcres -= net
      }
    }
  }

  const nriOwned = kind === "mineral" ? 0 : acqAcres > 0 ? wNri / acqAcres : baseNri
  const assigned = thisRow ? num(thisRow.nri_assigned_percent) / 100 : 0
  const orriReserved = kind === "mineral" ? 0 : Math.max(nriOwned - assigned, 0)

  // hasPosition mirrors the SERVER rule ("any ACQ ever"): a base inventory row
  // OR any batch ACQ for the STR grants a position, even at net 0.
  const batchAcq = target
    ? rows.some((r) => r.id !== thisRowId && r.mode === "ACQ" && normalizeStr(r.str) === target)
    : false
  const hasPosition = hadBasePosition || batchAcq

  // Predicted DIV status — a display-only mirror of the server rule in
  // TransactionBatchCreator#derived_div_interest_type (MUST stay in lockstep):
  // candidates are the persisted latest ACQ (seq -1, only when it exists) plus
  // every OTHER in-grid ACQ for the STR (seq = grid index); the latest by
  // (date, seq) wins and its status — even nil — is the answer.
  type DivCandidate = [date: string, seq: number, status: string | null]
  const candidates: DivCandidate[] = []
  if (k?.latest_acq_date) candidates.push([k.latest_acq_date, -1, k.latest_acq_status ?? null])
  if (target) {
    rows.forEach((r, i) => {
      if (r.id === thisRowId || r.mode !== "ACQ") return
      if (normalizeStr(r.str) !== target) return
      // The server drops candidates whose date won't cast — mirror that.
      if (!r.transaction_date) return
      candidates.push([r.transaction_date, i, r.interest_type || null])
    })
  }
  let latest: DivCandidate | null = null
  for (const c of candidates) {
    // ISO yyyy-mm-dd dates compare correctly as strings.
    if (!latest || c[0] > latest[0] || (c[0] === latest[0] && c[1] > latest[1])) latest = c
  }
  const divStatus = latest ? latest[2] : null

  return { naOwned: netAcres, nriOwned, orriReserved, hasPosition, divStatus }
}

// A row is "empty" (skipped on submit) when every USER-editable field is blank.
// Date (defaulted to today), mode, interest_type, and expanded are ignored.
export function isRowEmpty(r: RowState): boolean {
  return (
    r.str.trim() === "" &&
    r.county.trim() === "" &&
    r.net_acres.trim() === "" &&
    r.nri_percent.trim() === "" &&
    r.nri_assigned_percent.trim() === "" &&
    r.manual_orri_percent.trim() === "" &&
    r.arti === false &&
    r.override_orri === false &&
    r.counterparty.trim() === "" &&
    r.depths.trim() === "" &&
    r.price.trim() === "" &&
    r.book_page.trim() === "" &&
    r.expiration_date.trim() === "" &&
    r.orri_640.trim() === "" &&
    r.royalty.trim() === "" &&
    r.legal_desc.trim() === "" &&
    r.ogl_lessor.trim() === "" &&
    r.ogl_lessee.trim() === "" &&
    r.ogl_date.trim() === "" &&
    r.ogl_term.trim() === "" &&
    r.ogl_book_page.trim() === "" &&
    r.notes.trim() === ""
  )
}

export function updateRow(rows: RowState[], id: string, patch: Partial<RowState>): RowState[] {
  return rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
}

export interface BatchTxPayload {
  type: string
  str: string
  interest_kind: InterestKind
  transaction_date: string | null
  net_acres: number
  nri?: number // leasehold only
  orri_reserved: number
  arti?: boolean
  interest_type: string | null
  county: string | null
  depths: string | null
  price: number | null
  counterparty: string | null
  book_page: string | null
  notes: string | null
  expiration_date?: string | null
  orri_640?: number | null
  royalty?: number | null
  legal_desc?: string | null
  ogl_lessor?: string | null
  ogl_lessee?: string | null
  ogl_date?: string | null
  ogl_term?: string | null
  ogl_book_page?: string | null
}

// Build the API payload for the non-empty rows, plus the id list so 422 errors
// (indexed over submitted rows) map back to the right grid rows.
export function buildBatchPayload(
  rows: RowState[],
  effectivePositionFor: (row: RowState) => EffectivePosition,
  kind: InterestKind
): { payload: { transactions: BatchTxPayload[] }; submittedRowIds: string[] } {
  const submitted = rows.filter((r) => !isRowEmpty(r))
  const transactions = submitted.map<BatchTxPayload>((r) => {
    const pos = effectivePositionFor(r)
    // ARTI divestitures display Net Acres from the derived position but never
    // store it in row.net_acres — read it from the position at build time.
    const netAcres = r.mode === "DIV" && r.arti ? pos.naOwned : Number(r.net_acres)
    const base = {
      str: r.str.trim(),
      interest_kind: kind,
      transaction_date: r.transaction_date || null,
      net_acres: netAcres,
      county: r.county || null,
      depths: r.depths || null,
      price: r.price ? Number(r.price) : null,
      counterparty: r.counterparty || null,
      book_page: r.book_page || null,
      notes: r.notes || null,
    }
    // Only PT acquisitions carry a lease expiration. DIV rows send
    // interest_type: null — the server derives it from the latest ACQ.
    const expirationDate = r.interest_type === "PT" ? r.expiration_date || null : null
    if (kind === "leasehold") {
      return r.mode === "ACQ"
        ? {
            ...base,
            type: "ACQ",
            interest_type: r.interest_type || null,
            nri: Number(r.nri_percent) / 100,
            orri_reserved: 0,
            orri_640: r.orri_640 ? Number(r.orri_640) : null,
            expiration_date: expirationDate,
          }
        : {
            ...base,
            type: "DIV",
            interest_type: null,
            nri: Number(r.nri_assigned_percent) / 100,
            orri_reserved: r.override_orri ? Number(r.manual_orri_percent) / 100 : pos.orriReserved,
            arti: r.arti,
          }
    }
    // Mineral: no NRI concept, no depth carve-outs; OGL detail only travels on
    // leased (PT/HBP) acquisitions. Royalty is a fraction (1.00 = 100%) — Open
    // means unleased, i.e. the full royalty.
    const leased = r.interest_type === "PT" || r.interest_type === "HBP"
    return r.mode === "ACQ"
      ? {
          ...base,
          type: "ACQ",
          interest_type: r.interest_type || null,
          orri_reserved: 0,
          royalty: r.interest_type === "Open" ? FULL_ROYALTY : r.royalty ? Number(r.royalty) : null,
          legal_desc: r.legal_desc || null,
          depths: null,
          expiration_date: expirationDate,
          ogl_lessor: leased ? r.ogl_lessor || null : null,
          ogl_lessee: leased ? r.ogl_lessee || null : null,
          ogl_date: leased ? r.ogl_date || null : null,
          ogl_term: leased ? r.ogl_term || null : null,
          ogl_book_page: leased ? r.ogl_book_page || null : null,
        }
      : {
          ...base,
          type: "DIV",
          interest_type: null,
          orri_reserved: 0,
          legal_desc: r.legal_desc || null,
          depths: null,
          arti: r.arti,
        }
  })
  return { payload: { transactions }, submittedRowIds: submitted.map((r) => r.id) }
}

export interface BatchError {
  index: number | null
  messages: string[]
}

export function mapBatchErrorsToRows(
  errors: BatchError[],
  submittedRowIds: string[]
): Record<string, string[]> {
  const byRow: Record<string, string[]> = {}
  for (const e of errors) {
    if (e.index == null) continue
    const id = submittedRowIds[e.index]
    if (id) byRow[id] = e.messages
  }
  return byRow
}
